/**
 * WebSocket hook for realtime collaboration.
 * Handles connection, sending/receiving operations, and presence.
 */
import { useCallback, useEffect, useRef, useState } from "react";

export type Operation = {
  type: string;
  op_id: string;
  ts: string;
  session_id: string;
  [key: string]: any;
};

export type PresenceUser = {
  session_id: string;
  user_name?: string;
};

type WebSocketStatus = "connecting" | "connected" | "disconnected" | "error";

type UseWebSocketOptions = {
  structureId: string;
  enabled?: boolean;
  onOperation?: (op: Operation) => void;
  authToken?: string | null;
  shareToken?: string | null;
};

const WS_URL = import.meta.env.VITE_WS_URL || "ws://localhost:8000";

export const useWebSocket = ({ structureId, enabled = true, onOperation, authToken, shareToken }: UseWebSocketOptions) => {
  const [status, setStatus] = useState<WebSocketStatus>("disconnected");
  const [presenceUsers, setPresenceUsers] = useState<PresenceUser[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const presenceMapRef = useRef<Map<string, PresenceUser>>(new Map());
  const sessionIdRef = useRef<string | null>(null);

  const connect = useCallback(() => {
    if (!enabled || wsRef.current) return;

    setStatus("connecting");

    const params = new URLSearchParams();
    if (authToken) params.set("token", authToken);
    if (shareToken) params.set("share_token", shareToken);
    const url = `${WS_URL}/api/ws/structures/${structureId}${params.toString() ? `?${params.toString()}` : ""}`;
    const ws = new WebSocket(url);

    ws.onopen = () => {
      console.log("[WS] Connected to", structureId);
      setStatus("connected");
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        // Handle different message types
        if (data.type === "presence_join") {
          presenceMapRef.current.set(data.session_id, {
            session_id: data.session_id,
            user_name: data.user_name,
          });
          setPresenceUsers(Array.from(presenceMapRef.current.values()));
          console.log("[WS] User joined:", data.session_id);

          // First presence_join we receive is our own session
          if (!sessionIdRef.current) {
            sessionIdRef.current = data.session_id;
            setSessionId(data.session_id);
            console.log("[WS] My session ID:", data.session_id);
          }
        } else if (data.type === "presence_leave") {
          presenceMapRef.current.delete(data.session_id);
          setPresenceUsers(Array.from(presenceMapRef.current.values()));
          console.log("[WS] User left:", data.session_id);
        } else if (data.type === "ack") {
          console.log("[WS] Ack received for op:", data.op_id);
        } else if (data.type === "error") {
          console.error("[WS] Error from server:", data.message);
        } else {
          // Regular operation - ignore own operations
          if (data.session_id !== sessionIdRef.current && onOperation) {
            onOperation(data as Operation);
          } else if (data.session_id === sessionIdRef.current) {
            console.log("[WS] Ignoring own operation:", data.type);
          }
        }
      } catch (err) {
        console.error("[WS] Failed to parse message:", err);
      }
    };

    ws.onerror = (err) => {
      console.error("[WS] Error:", err);
      setStatus("error");
    };

    ws.onclose = () => {
      console.log("[WS] Disconnected");
      setStatus("disconnected");
      wsRef.current = null;

      // Auto-reconnect after 3s
      if (enabled) {
        reconnectTimeoutRef.current = window.setTimeout(() => {
          console.log("[WS] Reconnecting...");
          connect();
        }, 3000);
      }
    };

    wsRef.current = ws;
  }, [authToken, enabled, onOperation, shareToken, structureId]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    setStatus("disconnected");
    presenceMapRef.current.clear();
    setPresenceUsers([]);
    sessionIdRef.current = null;
    setSessionId(null);
  }, []);

  const sendOperation = useCallback((operation: Partial<Operation>) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.warn("[WS] Cannot send, not connected");
      return false;
    }

    try {
      const op = {
        ...operation,
        op_id: operation.op_id || crypto.randomUUID(),
        ts: operation.ts || new Date().toISOString(),
        session_id: sessionIdRef.current || "unknown",
      };

      wsRef.current.send(JSON.stringify({ operation: op }));
      console.log("[WS] Sent operation:", op.type, "from session:", sessionIdRef.current);
      return true;
    } catch (err) {
      console.error("[WS] Failed to send operation:", err);
      return false;
    }
  }, []);

  useEffect(() => {
    if (enabled) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [enabled, connect, disconnect]);

  // Extract session_id from presence_join for self
  useEffect(() => {
    if (presenceUsers.length > 0 && !sessionId) {
      // Heuristic: last joined user is likely us
      const lastUser = presenceUsers[presenceUsers.length - 1];
      setSessionId(lastUser.session_id);
    }
  }, [presenceUsers, sessionId]);

  return {
    status,
    isConnected: status === "connected",
    presenceUsers,
    presenceCount: presenceUsers.length,
    sessionId,
    sendOperation,
    connect,
    disconnect,
  };
};
